import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@core/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@core/components/ui/dialog";
import { toast } from "@core/components/ui/use-toast";
import { PaymentBreakdown } from "@phoenix/invoicing/routes/_site.dashboard.invoices.route/PaymentBreakdown";
import { PayOnAccount } from "@phoenix/invoicing/routes/_site.dashboard.invoices.route/PayOnAccount";
import { Button } from "@core/components/ui/button";
import { SubmissionOverlay, SubmissionOverlayState } from "../../components/SubmissionOverlay";

type PayOnAccountDialogProps = {
  /** When provided, dialog open state is controlled by the parent (e.g. from URL openPayOnAccount=1). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/** How long the success state lingers before the dialog gracefully closes itself. */
const SUCCESS_AUTO_CLOSE_MS = 4000;

export const PayOnAccountDialog = (props: PayOnAccountDialogProps) => {
  const { open: controlledOpen, onOpenChange: controlledOnOpenChange } = props;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOnOpenChange != null;
  const open = isControlled ? (controlledOpen ?? false) : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange : setInternalOpen;
  // Keep a ref to the latest setOpen so timers can close the dialog without
  // re-subscribing when a parent passes a new onOpenChange identity each render.
  const setOpenRef = React.useRef(setOpen);
  setOpenRef.current = setOpen;
  const autoCloseRef = React.useRef<number | null>(null);
  const api = useApi();
  const { t } = useTranslation();
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  const [submissionState, setSubmissionState] = React.useState<SubmissionOverlayState>("idle");
  const submitWatchdogRef = React.useRef<number | null>(null);
  const [paymentsApplied, setPaymentsApplied] = React.useState<
    Record<string, number>
  >({});

  const loadPayOnAccountData = React.useCallback(async () => {
    const reportsPromise = api.GetDashboardReports().catch(() => []);
    const [reports, methods, invoiceResult] = await Promise.all([
      reportsPromise,
      api.GetCurrentUserPaymentMethods(),
      api.ListSalesCollectionsForCurrentCustomer({
        TypeId: "Invoice",
        StatusId: ["Unpaid", "PartiallyPaid"],
        "sort.CreatedDate": "Descending",
        PageIndex: 0,
        PageSize: 5000,
      }),
    ]);
    const openInvoices = (invoiceResult?.Results ?? []).filter(
      (invoice: any) => invoice?.StatusId === "Unpaid" || invoice?.StatusId === "PartiallyPaid",
    );
    const allInvoiceIds = openInvoices.map((invoice: any) => invoice.Id);
    const invoiceBalances = allInvoiceIds.length > 0
      ? await api["/invoicing/calculatebalancedue"](allInvoiceIds)
      : {};
    const calculatedTotalBalance = allInvoiceIds.reduce(
      (sum: number, id: number) => sum + (invoiceBalances[id] ?? 0),
      0,
    );
    const accountBalanceReport = (reports ?? []).find((r: any) =>
      r?.To === "/dashboard/invoices?openPayOnAccount=1"
      || String(r?.Label ?? "").toLowerCase().includes("account balance"),
    );
    const reportedBalance = accountBalanceReport?.Amount
      ? Number(String(accountBalanceReport.Amount).replace(/[^0-9.-]/g, ""))
      : null;
    const totalBalance = calculatedTotalBalance > 0
      ? calculatedTotalBalance
      : (Number.isFinite(reportedBalance) ? reportedBalance : 0);
    const invoiceKeyById = openInvoices.reduce((acc: Record<number, string>, invoice: any) => {
      acc[invoice.Id] = invoice.Key ?? `#${invoice.Id}`;
      return acc;
    }, {});
    return { methods, totalBalance, invoiceKeyById };
  }, [api]);

  React.useEffect(() => {
    if (open) return;
    setShowBreakdown(false);
    setPaymentsApplied({});
    setSubmissionState("idle");
    if (submitWatchdogRef.current) {
      window.clearInterval(submitWatchdogRef.current);
      submitWatchdogRef.current = null;
    }
    if (autoCloseRef.current) {
      window.clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }
  }, [open]);

  // Once a payment succeeds (breakdown shown, or watchdog flipped to success),
  // gracefully close the dialog after a short debounce so the success surfaces
  // never get stuck on screen.
  React.useEffect(() => {
    const successShowing = submissionState === "success" || showBreakdown;
    if (!successShowing) return;
    if (autoCloseRef.current) return;
    autoCloseRef.current = window.setTimeout(() => {
      autoCloseRef.current = null;
      setOpenRef.current(false);
    }, SUCCESS_AUTO_CLOSE_MS);
    return () => {
      if (autoCloseRef.current) {
        window.clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    };
  }, [submissionState, showBreakdown]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["PayOnAccountData"],
    queryFn: loadPayOnAccountData,
  });

  const stopSubmitWatchdog = React.useCallback(() => {
    if (submitWatchdogRef.current) {
      window.clearInterval(submitWatchdogRef.current);
      submitWatchdogRef.current = null;
    }
  }, []);

  const startSubmitWatchdog = React.useCallback((initialTotalBalance: number) => {
    stopSubmitWatchdog();
    submitWatchdogRef.current = window.setInterval(async () => {
      try {
        const refreshed = await loadPayOnAccountData();
        const currentTotal = refreshed.totalBalance;
        if (currentTotal <= initialTotalBalance - 0.009) {
          setSubmissionState("success");
          stopSubmitWatchdog();
        }
      } catch {
        // ignore polling errors
      }
    }, 2000);
  }, [loadPayOnAccountData, stopSubmitWatchdog]);

  const handleSubmit = async (
    amount: number,
    paymentMethodId: number,
    expectedTotal: number
  ) => {
    const payload = {
      WalletId: paymentMethodId,
      Total: Number(amount.toFixed(2)),
      ExpectedTotal: Number(expectedTotal.toFixed(2)),
    };
    setSubmissionState("submitting");
    startSubmitWatchdog(data?.totalBalance ?? 0);
    try {
      const result = await api.PayOnAccount(payload);
      stopSubmitWatchdog();
      if (result.IsSuccess && result.Result) {
        setPaymentsApplied(result.Result.PaymentsApplied);
        setShowBreakdown(true);
        // Clear the submitting/watchdog overlay so it can't sit on top of the
        // breakdown; the breakdown is now the success surface.
        setSubmissionState("idle");
      } else {
        setSubmissionState("idle");
        toast({
          title: "Payment Failed",
          description: result.Messages?.join(", ") || "An error occurred.",
          variant: "destructive",
        });
      }
    } catch {
      stopSubmitWatchdog();
      setSubmissionState("idle");
      toast({
        title: "Network Error",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="primary" type="button">
          {t("ui.admin.common.PayOnAccount", "Pay on Account")}
        </Button>
      </DialogTrigger>
      {!(isLoading || isError || !data) && (
        <DialogContent className="relative max-h-[90vh] overflow-y-auto w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {t("ui.admin.common.PayOnAccount", "Pay on Account")}
            </DialogTitle>
          </DialogHeader>
          <SubmissionOverlay
            state={submissionState}
            onClose={() => setOpen(false)}
          />
          {!showBreakdown ? (
            <PayOnAccount
              dialogOpen={open}
              onSubmit={handleSubmit}
              methods={data.methods.Results}
              totalBalance={data.totalBalance}
            />
          ) : (
            <PaymentBreakdown
              payments={paymentsApplied}
              invoiceKeyById={data.invoiceKeyById}
              onClose={() => setOpen(false)}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};
